/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable @typescript-eslint/return-await */
/* eslint-disable arrow-body-style */
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios/index";
import {
  IDeleteParams,
  IPostDynamicUrlParams,
  IGetItemParams,
  IGetListParams,
  IPatchParams,
  IPostParams,
} from "src/api/api";
import { useSnackbar } from "src/components/snackbar";
import axiosInstance from "src/utils/axios";

interface AxiosErrorT {
  errors: { code: string; message: string }[];
}

const useApiServices = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Get list service
  const useGetListApi = <T, ParamsType = any>({
    parameters,
    url,
    onSuccess,
    enabled,
  }: IGetListParams<ParamsType>) => {
    return useQuery<T, AxiosError<AxiosErrorT, AxiosErrorT>>(
      [url, parameters],
      async () => await axiosInstance.get(url, { withCredentials: true, params: parameters }),
      {
        onSuccess,
        enabled,
      }
    );
  };

  // Get item service
  const useGetItemApi = <T,>({
    url,
    id,
    onSuccess,
    onError,
    enabled,
    refetchInterval,
  }: IGetItemParams) => {
    return useQuery<T, AxiosError>(
      [url],
      async () => await axiosInstance.get(`${url}${id ? `/${id}` : ""}`),
      {
        onSuccess,
        onError,
        enabled,
        refetchInterval,
      }
    );
  };

  // Delete service
  const useDeleteApi = <T,>({
    url,
    urlAfterSuccess,
    onSuccess,
    onError,
    withSuccessNotistack = true,
    withErrorNotistack = true,
  }: IDeleteParams) => {
    const queryClient = useQueryClient();
    return useMutation<T, AxiosError, T>((id) => axiosInstance.delete(`${url}/${id}`), {
      onSuccess: (data) => {
        queryClient.invalidateQueries([urlAfterSuccess ?? url]);
        withSuccessNotistack && handleApiSuccesswithNotistack();
        onSuccess && onSuccess(data);
      },
      onError: (error) => {
        withErrorNotistack && handleApiErrorwithNotistack(error);
        onError && onError(error);
      },
    });
  };

  // Post service
  const usePostApi = <T, U>({ url, urlAfterSuccess, onSuccess, onError }: IPostParams) => {
    const queryClient = useQueryClient();
    return useMutation<T, AxiosError, U>(
      (requestData: U) => {
        return axiosInstance.post(url, requestData);
      },
      {
        onSuccess: (data: T) => {
          queryClient.invalidateQueries([urlAfterSuccess ?? url]);
          onSuccess && onSuccess(data);
        },
        onError: (error: AxiosError) => {
          onError && onError(error);
        },
      }
    );
  };

  const usePostDynamicUrlApi = <T, U>({
    buildUrl,
    requestBody,
    urlAfterSuccess,
    onSuccess,
    onError,
  }: IPostDynamicUrlParams<U>) => {
    const queryClient = useQueryClient();
    return useMutation<T, AxiosError, U>(
      (variables) => axiosInstance.post(buildUrl(variables), requestBody),
      {
        onSuccess: (data: T) => {
          urlAfterSuccess && queryClient.invalidateQueries([urlAfterSuccess]);
          onSuccess && onSuccess(data);
        },
        onError: (error: AxiosError) => {
          onError && onError(error);
        },
      }
    );
  };

  // Patch service
  const usePatchApi = <T,>({
    url,
    id,
    urlAfterSuccess,
    onSuccess,
    onError,
    withSuccessNotistack = true,
    withErrorNotistack = true,
  }: IPatchParams) => {
    const queryClient = useQueryClient();
    return useMutation<T, AxiosError, T>(
      (requestData) => axiosInstance.patch(id ? `${url}/${id}` : url, requestData),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([urlAfterSuccess ?? url]);
          withSuccessNotistack && handleApiSuccesswithNotistack();
          onSuccess && onSuccess();
        },
        onError: (error) => {
          withErrorNotistack && handleApiErrorwithNotistack(error);
          onError && onError(error);
        },
      }
    );
  };

  // For show snackbar when api return a error
  const handleApiErrorwithNotistack = (error: AxiosError) => {};

  // For show snackbar when api successfully
  const handleApiSuccesswithNotistack = () => {};

  return {
    useGetListApi,
    useGetItemApi,
    usePostApi,
    usePostDynamicUrlApi,
    usePatchApi,
    useDeleteApi,
  };
};

export default useApiServices;
